import React from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import classNames from 'classnames';
import { nl, enUS } from 'date-fns/locale';
import useLanguage from '../../hooks/useLanguage';
import { Language } from '../../types/requests';

registerLocale('nl', nl);
registerLocale('en', enUS);

interface DateTimePickerProps {
  value?: Date | undefined | null;
  onChange: (date?: Date | undefined | null) => void;
  inline?: boolean;
  timeEnabled?: boolean;
  dateOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
  isClearable?: boolean;
  label?: string;
  required?: boolean;
}

const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, className, ariaLabel }, ref) => (
    <input
      ref={ref}
      readOnly
      onClick={onClick}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={classNames('!bg-secondary-50', className)}
    />
  ),
);
CustomInput.displayName = 'CustomInput';

export default function DateTimePicker({
  value,
  onChange,
  inline = false,
  timeEnabled = false,
  dateOnly = false,
  minDate,
  maxDate,
  className,
  placeholder,
  ariaLabel,
  isClearable = true,
  label,
  required = false,
}: DateTimePickerProps) {
  const language = useLanguage();
  const locale = language === Language.EN ? 'en' : 'nl';
  const timeCaption = language === Language.EN ? 'Time' : 'Tijd';

  return (
    <div className="flex flex-col">
      {label && (
        <label className="mb-1 pr-2 font-medium text-primary-900">
          {label}
          {required && '*'}
        </label>
      )}
      <ReactDatePicker
        selected={value || null}
        onChange={(date) => onChange(date ?? undefined)}
        showTimeSelect={timeEnabled && !dateOnly}
        timeFormat="HH:mm"
        timeIntervals={5}
        timeCaption={timeCaption}
        dateFormat={timeEnabled && !dateOnly ? 'dd-MM-yyyy HH:mm' : 'dd-MM-yyyy'}
        inline={inline}
        minDate={minDate}
        maxDate={maxDate}
        customInput={<CustomInput placeholder={placeholder} className={className} ariaLabel={ariaLabel} />}
        placeholderText={placeholder}
        isClearable={isClearable}
        calendarClassName="custom-calendar"
        popperClassName="custom-popper"
        showPopperArrow={false}
        locale={locale}
      />
    </div>
  );
}
